import React, { useState } from 'react'
import {
  Box, Button, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, FormControl, InputLabel, Select, MenuItem, Alert
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import { router, usePage } from '@inertiajs/react'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import StatusBadge from '../../../../components/ui/StatusBadge'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'

Show.layout = (page) => <AppShell>{page}</AppShell>

export default function Show({ property, payment, allocations = [], available_contracts = [] }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}`
  const [selectedContractId, setSelectedContractId] = useState('')

  const handleDelete = () => {
    if (window.confirm('Zahlung wirklich löschen?')) {
      router.delete(`${basePath}/payments/${payment.id}`)
    }
  }

  const handleAllocate = () => {
    router.patch(`${basePath}/payments/${payment.id}/allocate`)
  }

  const handleAssignContract = () => {
    if (!selectedContractId) return
    router.patch(`${basePath}/payments/${payment.id}/assign_contract`, {
      payment: { contract_id: selectedContractId }
    })
  }

  const isOpen = payment.status === 'open'
  const isInbound = payment.direction === 'inbound'
  const hasContract = !!payment.contract_id

  return (
    <Box>
      <PageHeader
        title={`Zahlung: ${payment.amount} €`}
        breadcrumbs={[
          { label: property.name, href: basePath },
          { label: 'Zahlungen', href: `${basePath}/payments` },
          { label: `${payment.amount} €` },
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isOpen && (
              <>
                <Button variant="outlined" size="small" startIcon={<EditIcon />}
                  onClick={() => router.visit(`${basePath}/payments/${payment.id}/edit`)}
                  data-testid="edit-btn"
                >
                  Bearbeiten
                </Button>
                <Button variant="outlined" size="small" color="error" startIcon={<DeleteIcon />}
                  onClick={handleDelete} data-testid="delete-btn"
                >
                  Löschen
                </Button>
                {isInbound && hasContract && (
                  <Button variant="contained" size="small" startIcon={<PlaylistAddCheckIcon />}
                    onClick={handleAllocate} data-testid="allocate-btn"
                  >
                    Zuordnen
                  </Button>
                )}
              </>
            )}
          </Box>
        }
      />

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatusBadge type="payment_direction" value={payment.direction} />
          <StatusBadge type="payment_status" value={payment.status} />
          <Typography variant="body2" color="text.secondary">Datum: {payment.booking_date}</Typography>
          {payment.contact_name && (
            <Typography variant="body2" color="text.secondary">Kontakt: {payment.contact_name}</Typography>
          )}
          {payment.bank_account_name && (
            <Typography variant="body2" color="text.secondary">Konto: {payment.bank_account_name}</Typography>
          )}
        </Box>
        {payment.reference_text && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Verwendungszweck: {payment.reference_text}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
          <Typography variant="body2" fontWeight={600}>Betrag: <FormattedCurrency value={payment.amount} align="left" /></Typography>
          {payment.allocated_amount && (
            <Typography variant="body2" color="text.secondary">Zugeordnet: <FormattedCurrency value={payment.allocated_amount} align="left" /></Typography>
          )}
          {payment.unallocated_amount && (
            <Typography variant="body2" color="text.secondary">Offen: <FormattedCurrency value={payment.unallocated_amount} align="left" /></Typography>
          )}
        </Box>
      </Paper>

      {isOpen && isInbound && !hasContract && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'warning.light' }} data-testid="assign-contract-panel">
          <Alert severity="warning" sx={{ mb: 2 }}>
            Dieser Zahlungseingang konnte keinem Vertrag automatisch zugeordnet werden.
            Bitte wählen Sie den passenden Vertrag aus.
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 320 }}>
              <InputLabel id="contract-select-label">Vertrag</InputLabel>
              <Select
                labelId="contract-select-label"
                label="Vertrag"
                value={selectedContractId}
                onChange={(e) => setSelectedContractId(e.target.value)}
                data-testid="contract-select"
                slotProps={{ input: { 'data-testid': 'contract-select-input' } }}
              >
                {available_contracts.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained" size="small"
              onClick={handleAssignContract}
              disabled={!selectedContractId}
              data-testid="assign-contract-btn"
            >
              Speichern &amp; Zuordnen
            </Button>
          </Box>
        </Paper>
      )}

      {allocations.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Zuordnungen</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Zeitraum</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Sollstellung</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Rechnung</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Betrag</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allocations.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{a.period}</TableCell>
                    <TableCell>{a.receivable_description || '—'}</TableCell>
                    <TableCell>{a.invoice_number || '—'}</TableCell>
                    <TableCell align="right"><FormattedCurrency value={a.amount} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  )
}
